import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Container } from '@src/components/Container';
import { Button } from '@src/components/Button';
import { Divider } from '@src/components/Divider';
import InviteTeamModalContent from '@src/containers/InviteTeamModalContent';
import api from '@src/util/api';
import { setErrorFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { setModalContent } from '@src/redux/actions/modal';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { trackEvent } from '@src/metrics';
import { useCompanyTeamMembersQuery } from '@src/graphql/generated';
import { ContainerType } from '@src/components/Container/enums/containerType.enum';
import { InviteList } from './InviteList';
import { MembersList } from './MembersList';
import './styles.scss';

const CAN_INVITE_TEAM =
  "Invite team members to your company account who will have access to your company's open positions and candidates. To invite someone with collaborator/hiring manager access, use the invite functionality under a specific open position.";
const CAN_NOT_INVITE_TEAM = 'New team members can be invited by company admin.';

const SettingsTeam = ({ inviteTeam, setErrorFlashMessage, setSuccessFlashMessage, setModalContent }) => {
  const { loading, error, data } = useCompanyTeamMembersQuery({
    onError: () => setErrorFlashMessage(),
  });
  const [invites, setInvites] = useState(null);

  async function fetchInvites() {
    // if user has no right to invite a team member, do not fetch invites list
    if (inviteTeam) {
      try {
        const { invites } = await api('/api/team/invites');
        setInvites(invites);
      } catch (error) {
        setErrorFlashMessage();
      }
    }
  }

  useEffect(() => {
    fetchInvites();
  }, []);

  if (loading) {
    return null;
  }

  if (error) {
    return null;
  }

  const {
    company: { teamMembers },
  } = data;

  return (
    <Container className="settings-team" type={ContainerType.BORDERED}>
      <div className="settings-team__header">
        <Divider
          className="settings-team__header-title"
          text={inviteTeam ? CAN_INVITE_TEAM : CAN_NOT_INVITE_TEAM}
          title="Active members"
        />

        {inviteTeam && (
          <Button
            className="settings-team__header-button"
            onClick={() => {
              setModalContent({
                content: <InviteTeamModalContent onSuccess={() => fetchInvites()} />,
              });

              trackEvent({
                context: MetricContext.INVITING_TEAM_MEMBERS,
                interaction: MetricAction.OPEN_INVITE_TEAM_MEMBER,
                payload: {
                  'Event Source': MetricEventSource.TABLE_HEADER_BUTTON,
                  'Previous Context': MetricContext.TEAM_MEMBERS,
                },
              });
            }}
          >
            Invite people
          </Button>
        )}
      </div>

      <MembersList teamMembers={teamMembers} />

      <div className="settings-team__table-footer">To change a role please contact our support.</div>

      {inviteTeam && (
        <Fragment>
          <Divider
            className="settings-team__pending-title"
            text="Invited team members who will have access to your company account."
            title="Pending invite"
          />

          <InviteList
            invites={invites}
            setErrorFlashMessage={setErrorFlashMessage}
            setSuccessFlashMessage={setSuccessFlashMessage}
          />
        </Fragment>
      )}
    </Container>
  );
};

SettingsTeam.propTypes = {
  inviteTeam: PropTypes.bool.isRequired,
  setErrorFlashMessage: PropTypes.func.isRequired,
  setModalContent: PropTypes.func.isRequired,
  setSuccessFlashMessage: PropTypes.func.isRequired,
};

export default connect(null, { setErrorFlashMessage, setModalContent, setSuccessFlashMessage })(SettingsTeam);
